
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingAccountErrorResponse struct
 */
class TradingAccountErrorResponse {
  /**
   * Initialize struct
   * @param {!string=} id
   * @param {!boolean=} succeeded
   * @param {!string=} errorCode
   * @param {!string=} errorDescription
   * @constructor
   */
  constructor (id = '', succeeded = false, errorCode = '', errorDescription = '') {
    this.id = id
    this.succeeded = succeeded
    this.errorCode = errorCode
    this.errorDescription = errorDescription
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingAccountErrorResponse}
   * @param {!TradingAccountErrorResponse} other Other struct
   * @returns {!TradingAccountErrorResponse} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = other.id
    } else {
      this.id = null
    }
    if (other.succeeded != null) {
      this.succeeded = other.succeeded
    } else {
      this.succeeded = null
    }
    if (other.errorCode != null) {
      this.errorCode = other.errorCode
    } else {
      this.errorCode = null
    }
    if (other.errorDescription != null) {
      this.errorDescription = other.errorDescription
    } else {
      this.errorDescription = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingAccountErrorResponse}
   * @returns {!TradingAccountErrorResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingAccountErrorResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingAccountErrorResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingAccountErrorResponse}
   * @param {!TradingAccountErrorResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingAccountErrorResponse)) {
      throw new TypeError('Instance of TradingAccountErrorResponse is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingAccountErrorResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id : null),
      succeeded: ((this.succeeded != null) ? this.succeeded : null),
      errorCode: ((this.errorCode != null) ? this.errorCode : null),
      errorDescription: ((this.errorDescription != null) ? this.errorDescription : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingAccountErrorResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingAccountErrorResponse} other Object value
   * @returns {!TradingAccountErrorResponse} Created struct
   */
  static fromObject (other) {
    return new TradingAccountErrorResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingAccountErrorResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingAccountErrorResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingAccountErrorResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10013
  }
}

export { TradingAccountErrorResponse };

/**
 * Fast Binary Encoding TradingAccountErrorResponse field model
 */
class FieldModelTradingAccountErrorResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelString(buffer, 4 + 4)
    this.succeeded = new fbe.FieldModelBool(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.errorCode = new fbe.FieldModelString(buffer, this.succeeded.fbeOffset + this.succeeded.fbeSize)
    this.errorDescription = new fbe.FieldModelString(buffer, this.errorCode.fbeOffset + this.errorCode.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.succeeded.fbeSize + this.errorCode.fbeSize + this.errorDescription.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.succeeded.fbeExtra + this.errorCode.fbeExtra + this.errorDescription.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingAccountErrorResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10013
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.succeeded.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.succeeded.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.succeeded.fbeSize

    if ((fbeCurrentSize + this.errorCode.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.errorCode.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.errorCode.fbeSize

    if ((fbeCurrentSize + this.errorDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.errorDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.errorDescription.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!TradingAccountErrorResponse} fbeValue Default value, defaults is new TradingAccountErrorResponse()
   * @returns {!TradingAccountErrorResponse} TradingAccountErrorResponse value
   */
  get (fbeValue = new TradingAccountErrorResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!TradingAccountErrorResponse} fbeValue TradingAccountErrorResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get()
    } else {
      fbeValue.id = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.succeeded.fbeSize) <= fbeStructSize) {
      fbeValue.succeeded = this.succeeded.get()
    } else {
      fbeValue.succeeded = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.succeeded.fbeSize

    if ((fbeCurrentSize + this.errorCode.fbeSize) <= fbeStructSize) {
      fbeValue.errorCode = this.errorCode.get()
    } else {
      fbeValue.errorCode = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.errorCode.fbeSize

    if ((fbeCurrentSize + this.errorDescription.fbeSize) <= fbeStructSize) {
      fbeValue.errorDescription = this.errorDescription.get()
    } else {
      fbeValue.errorDescription = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.errorDescription.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingAccountErrorResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {!TradingAccountErrorResponse} fbeValue TradingAccountErrorResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingAccountErrorResponse}
   * @param {TradingAccountErrorResponse} fbeValue TradingAccountErrorResponse value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.succeeded.set(fbeValue.succeeded)
    this.errorCode.set(fbeValue.errorCode)
    this.errorDescription.set(fbeValue.errorDescription)
  }
}

export { FieldModelTradingAccountErrorResponse };

/**
 * Fast Binary Encoding TradingAccountErrorResponse model
 */
class TradingAccountErrorResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingAccountErrorResponse(this.buffer, 4)
  }

  /**
   * Get the TradingAccountErrorResponse model
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!FieldModelTradingAccountErrorResponse} model TradingAccountErrorResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingAccountErrorResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingAccountErrorResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingAccountErrorResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingAccountErrorResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingAccountErrorResponseModel}
   * @param {!TradingAccountErrorResponse} value TradingAccountErrorResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingAccountErrorResponseModel}
   * @param {!TradingAccountErrorResponse} value TradingAccountErrorResponse value, defaults is new TradingAccountErrorResponse()
   * @return {!object} Deserialized TradingAccountErrorResponse value and its size
   */
  deserialize (value = new TradingAccountErrorResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingAccountErrorResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingAccountErrorResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingAccountErrorResponseModel}
   * @param {!number} prev Previous TradingAccountErrorResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingAccountErrorResponseModel };
